import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import AnimatedBackground from "@/components/AnimatedBackground";
import { ShieldCheck, ArrowLeft } from "lucide-react";

export default function Login() {
  const { login, login2fa } = useAuth();
  const navigate = useNavigate();

  // step 1: credentials
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  // step 2: 2fa
  const [pendingToken, setPendingToken] = useState(null);
  const [twoFaUsername, setTwoFaUsername] = useState("");
  const [code, setCode] = useState("");
  const codeInputRef = useRef(null);

  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (pendingToken && codeInputRef.current) codeInputRef.current.focus();
  }, [pendingToken]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const res = await login(username, password);
      if (res && res.needs_2fa) {
        setPendingToken(res.pending_token);
        setTwoFaUsername(res.username || username);
        setCode("");
        return;
      }
      navigate("/panel");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const onSubmit2fa = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login2fa(pendingToken, code);
      navigate("/panel");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 py-10">
      <AnimatedBackground />
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center text-center">
          <img
            src="/assets/gulyan-logo-tight.png"
            alt="Gulyan OSINT"
            className="w-56 md:w-64 h-auto object-contain drop-shadow-[0_0_30px_rgba(157,78,221,0.35)]"
          />
          <div className="text-[11px] tracking-[0.4em] text-[#9D4EDD] font-mono mt-2">
            {pendingToken ? "İKİ ADIMLI DOĞRULAMA" : "GİRİŞ TERMİNALİ"}
          </div>
        </div>

        {!pendingToken && (
          <form onSubmit={onSubmit} className="gc-card p-6 md:p-8" data-testid="login-form">
            <h1 className="font-heading text-3xl font-bold mb-1">Giriş Yap</h1>
            <p className="text-sm text-[#A1A1AA] mb-6">Hesabınızla oturum açın.</p>

            <label className="gc-label block mb-2">Kullanıcı Adı</label>
            <input
              data-testid="login-username-input"
              className="gc-input mb-4"
              placeholder="kullanici_adi"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              required
            />

            <label className="gc-label block mb-2">Şifre</label>
            <input
              data-testid="login-password-input"
              className="gc-input mb-4"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />

            {error && (
              <div
                data-testid="login-error"
                className="text-[#9D4EDD] text-sm font-mono border border-[#9D4EDD]/40 bg-[#9D4EDD]/5 p-3 mb-4"
              >
                {error}
              </div>
            )}

            <button
              data-testid="login-submit"
              type="submit"
              disabled={busy}
              className="gc-btn-primary w-full"
            >
              {busy ? "GİRİLİYOR…" : "GİRİŞ YAP"}
            </button>

            <div className="mt-6 text-sm text-[#A1A1AA] text-center">
              Hesabınız yok mu?{" "}
              <Link
                to="/register"
                data-testid="login-to-register"
                className="text-[#9D4EDD] hover:text-white"
              >
                Kayıt olun
              </Link>
            </div>
          </form>
        )}

        {pendingToken && (
          <form onSubmit={onSubmit2fa} className="gc-card p-6 md:p-8" data-testid="login-2fa-form">
            <div className="flex items-center gap-2 mb-3 text-[#9D4EDD]">
              <ShieldCheck size={18} />
              <h1 className="font-heading text-2xl font-bold text-white">Doğrulama Kodu</h1>
            </div>
            <p className="text-sm text-[#A1A1AA] mb-6">
              <span className="text-white font-mono">{twoFaUsername}</span> hesabı için Authy /
              Google Authenticator uygulamasındaki 6 haneli kodu girin. Yedek kod da kullanabilirsiniz.
            </p>

            <label className="gc-label block mb-2">Kod</label>
            <input
              ref={codeInputRef}
              data-testid="login-2fa-code-input"
              className="gc-input mb-4 font-mono text-center tracking-[0.4em] text-lg"
              placeholder="123 456"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^0-9A-Za-z-]/g, ""))}
              maxLength={10}
              autoComplete="one-time-code"
              inputMode="numeric"
              required
            />

            {error && (
              <div
                data-testid="login-2fa-error"
                className="text-[#9D4EDD] text-sm font-mono border border-[#9D4EDD]/40 bg-[#9D4EDD]/5 p-3 mb-4"
              >
                {error}
              </div>
            )}

            <button
              data-testid="login-2fa-submit"
              type="submit"
              disabled={busy || code.length < 6}
              className="gc-btn-primary w-full"
            >
              {busy ? "DOĞRULANIYOR…" : "DOĞRULA & GİRİŞ YAP"}
            </button>

            <button
              type="button"
              data-testid="login-2fa-back"
              className="mt-4 inline-flex items-center gap-2 text-xs font-mono text-[#A1A1AA] hover:text-white"
              onClick={() => {
                setPendingToken(null);
                setCode("");
                setError("");
              }}
            >
              <ArrowLeft size={12} /> Geri dön
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
